import { createTheme } from '@mui/material/styles'

/* Also see MuiStyles in src/styles.js for
specific cases where muiTheme can not be used */

declare module '@mui/material/styles' {
  interface Palette {
    basic: Partial<Palette['primary']>
    contextMenu: Partial<Palette['primary']>
  }

  interface PaletteOptions {
    basic?: Partial<PaletteOptions['primary']>
    contextMenu?: Partial<PaletteOptions['primary']>
  }
}

const baseTheme = createTheme({
  components: {
    MuiFormHelperText: { styleOverrides: { root: { marginTop: '-25px' } } },
    MuiSwitch: {
      styleOverrides: {
        root: {
          marginTop: 10,
          width: 72,
          height: 36,
          padding: 0,
          borderRadius: 100,
        },
        switchBase: {
          color: '#707974',
          '&.Mui-checked': {
            color: '#ffffff',
            opacity: 1,
            '&+.MuiSwitch-track': {
              opacity: 1,
            },
          },
          '&.Mui-disabled+.MuiSwitch-track': {
            opacity: 0.3,
          },
        },
        track: {
          backgroundColor: '#E6E0E9',
          border: '2px solid #707974',
          borderRadius: 100,
          opacity: 1,
        },
        thumb: {
          width: 24,
          height: 24,
          transform: 'translateY(-3px)',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          textTransform: 'none',
          '&.Mui-disabled': {
            opacity: 0.3,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 4,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          minWidth: 160,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 14,
          minHeight: 'auto',
          paddingTop: 6,
          paddingBottom: 6,
          paddingLeft: 12,
          paddingRight: 16,
        },
      },
    },
  },
})

const skbv_gf_theme = createTheme(baseTheme, {
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'aktiv-grotesk, sans-serif',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: '#707974',
          '&.Mui-checked': {
            color: '#ffffff',
          },
        },
        track: {
          backgroundColor: '#E6E0E9',
          border: '2px solid #707974',
        },
      },
    },
  },
  palette: {
    primary: {
      main: '#1b5f4b',
      light: '#438d6d',
    },
    secondary: {
      main: '#a5432f',
      light: '#ffb4a4',
      contrastText: '#6a3f32',
    },
    error: {
      main: '#e30613',
    },
    warning: {
      main: '#f0c8a0',
      light: '#fffaeb',
    },
    info: {
      main: '#4f504a',
      light: '#F9F9F9',
    },
    success: {
      main: '#438d6d',
      light: '#60745f',
    },
    background: {
      main: '#ebebdb',
    },
    backgroundVariant: {
      main: '#f2f2e6',
    },
    // Mirrors the background of Scensum's .campaign-text on product pages ($color-primary-lightgray)
    campaignBox: {
      main: '#f2f3ec',
    },
    contextMenu: {
      main: '#c1dfc4',
    },
    basic: {
      dark: '#000000',
    },
  },
  typography: {
    fontFamily: 'aktiv-grotesk, sans-serif',
  },
})

const bm_theme = createTheme(baseTheme, {
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Mulish, sans-serif',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: '#707974',
          '&.Mui-checked': {
            color: '#ffffff',
          },
        },
        track: {
          backgroundColor: '#E6E0E9',
          border: '2px solid #707974',
        },
      },
    },
  },
  palette: {
    primary: {
      main: '#30814b',
      light: '#42b167',
    },
    secondary: {
      main: '#eb001e',
      light: '#eb001e',
      contrastText: '#ffffff',
    },
    error: {
      main: '#e30613',
    },
    warning: {
      main: '#f0c8a0',
      light: '#ffebdb',
    },
    info: {
      main: '#444444',
      light: '#F9F9F9',
    },
    success: {
      main: '#30814b',
      light: '#60745f',
    },
    background: {
      main: '#DFE8E6',
    },
    backgroundVariant: {
      main: '#ffe632',
    },
    // Mirrors the background of Scensum's .campaign-text on product pages ($color-granit)
    campaignBox: {
      main: '#e0e0e0',
    },
    contextMenu: {
      main: '#c1dfc4',
    },
    basic: {
      dark: '#000000',
    },
  },
  typography: {
    fontFamily: 'Mulish, sans-serif',
  },
})

export const getMuiTheme = (channelId: string) => {
  switch (channelId) {
    case '1':
      return skbv_gf_theme
    case '2':
      return bm_theme
    case '4':
      return skbv_gf_theme
    case '9':
      return bm_theme
    default:
      console.error(`Theme for channelId ${channelId} not available -- skbv_gf_theme is used`)
      return skbv_gf_theme
  }
}
